// Static demo-mode fill-in data for Regularization.component.jsx (the
// employee-facing "my regularization requests" view).
// Shapes must match what RegularizationComponent's `regList` /
// `teamProjects` / `memberDetails` expect — see Regularization.config.jsx's
// `columnConfig` cell renderers, which resolve `row.projectId` against
// `teamProjects` ({id, data:{project_name}}) and `row.assignee` against
// `memberDetails` (uid → {userName, userEmailid}).

// Firestore Timestamp-like stub — Regularization.config.jsx's `formatDate`
// helper (via `toDateValue`) supports a `.toDate()` function, a JS Date, a
// serialized {seconds,nanoseconds} map, or an ISO string; a `.toDate()` stub
// covers every call site (`row.startTime`, `row.createdAt`/`row.requestDate`).
const toTimestamp = (isoString) => ({
  toDate: () => new Date(isoString),
});

// Consumed by Regularization.config.jsx's `columnConfig` cellRenderers:
// project (via projectId → teamProjects), taskName, taskHours/taskMinutes,
// startTime (task date), createdAt/requestDate (report date), teamName
// (department), assignee (→ memberDetails), status. Field names mirror the
// real doc shape written by RegularizationModal.component.jsx.
export const DEMO_REGULARIZATION_LIST = [
  {
    id: 'demo-reg-1',
    userId: 'demo-user',
    userName: 'Ryan Mitchell',
    projectId: 'demo-proj-1',
    taskID: 'demo-task-1',
    taskName: 'Homepage layout fixes',
    taskDetails: 'Forgot to clock in before starting work',
    taskHours: 3,
    taskMinutes: 30,
    assignee: 'demo-manager-1',
    teamId: 'demo-team-engineering',
    teamName: 'Engineering',
    status: 'Approved',
    startTime: toTimestamp('2026-07-14T09:00:00Z'),
    createdAt: toTimestamp('2026-07-14T18:00:00Z'),
    requestDate: toTimestamp('2026-07-14T18:00:00Z'),
  },
  {
    id: 'demo-reg-2',
    userId: 'demo-user',
    userName: 'Ryan Mitchell',
    projectId: 'demo-proj-1',
    taskID: 'demo-task-2',
    taskName: 'Client feedback review',
    taskDetails: 'Tracker crashed mid-session',
    taskHours: 2,
    taskMinutes: 0,
    assignee: 'demo-manager-1',
    teamId: 'demo-team-engineering',
    teamName: 'Engineering',
    status: 'Approved',
    startTime: toTimestamp('2026-07-12T13:00:00Z'),
    createdAt: toTimestamp('2026-07-12T19:00:00Z'),
    requestDate: toTimestamp('2026-07-12T19:00:00Z'),
  },
  {
    id: 'demo-reg-3',
    userId: 'demo-user',
    userName: 'Ryan Mitchell',
    projectId: 'demo-proj-2',
    taskID: 'demo-task-3',
    taskName: 'API integration debugging',
    taskDetails: 'Worked from a client site without the desktop app',
    taskHours: 4,
    taskMinutes: 15,
    assignee: 'demo-manager-2',
    teamId: 'demo-team-engineering',
    teamName: 'Engineering',
    status: 'Pending',
    startTime: toTimestamp('2026-07-10T10:00:00Z'),
    createdAt: toTimestamp('2026-07-10T17:00:00Z'),
    requestDate: toTimestamp('2026-07-10T17:00:00Z'),
  },
  {
    id: 'demo-reg-4',
    userId: 'demo-user',
    userName: 'Ryan Mitchell',
    projectId: 'demo-proj-3',
    taskID: 'demo-task-4',
    taskName: 'Missed clock-in - traffic delay',
    taskDetails: 'Commute delay, started work late without tracking',
    taskHours: 1,
    taskMinutes: 45,
    assignee: 'demo-manager-1',
    teamId: 'demo-team-engineering',
    teamName: 'Engineering',
    status: 'Declined',
    startTime: toTimestamp('2026-07-08T08:30:00Z'),
    createdAt: toTimestamp('2026-07-08T16:00:00Z'),
    requestDate: toTimestamp('2026-07-08T16:00:00Z'),
  },
  {
    id: 'demo-reg-5',
    userId: 'demo-user',
    userName: 'Ryan Mitchell',
    projectId: 'demo-proj-2',
    taskID: 'demo-task-5',
    taskName: 'Sprint planning prep',
    taskDetails: 'Prepared docs on personal laptop over the weekend',
    taskHours: 2,
    taskMinutes: 30,
    assignee: 'demo-manager-2',
    teamId: 'demo-team-engineering',
    teamName: 'Engineering',
    status: 'Pending',
    startTime: toTimestamp('2026-07-15T09:00:00Z'),
    createdAt: toTimestamp('2026-07-15T15:00:00Z'),
    requestDate: toTimestamp('2026-07-15T15:00:00Z'),
  },
];

// Consumed by Regularization.config.jsx's "Project" column
// (`teamProjects.find((p) => p.id === row.projectId)?.data?.project_name`).
export const DEMO_TEAM_PROJECTS = [
  {
    id: 'demo-proj-1',
    data: { project_name: 'Website Redesign', deleted: false },
  },
  { id: 'demo-proj-2', data: { project_name: 'Mobile App', deleted: false } },
  {
    id: 'demo-proj-3',
    data: { project_name: 'Internal Tools', deleted: false },
  },
];

// Consumed by Regularization.config.jsx's "Assignee" column
// (`memberDetails?.[row.assignee]` → userName || userEmailid) — matches the
// uid → details map shape produced by getTeamMemberDetails.
export const DEMO_MEMBER_DETAILS = {
  'demo-manager-1': {
    userName: 'Rachel Turner',
    userEmailid: 'rachel.turner@meridianlabs.com',
    userType: 'Manager',
  },
  'demo-manager-2': {
    userName: 'Emma Williams',
    userEmailid: 'emma.williams@meridianlabs.com',
    userType: 'Manager',
  },
};
